export type OrderType = 'asc' | 'desc';
type Order = OrderType;

type ComparatorResult = number;

export type Comparator<T> = (a: T, b: T) => ComparatorResult;

export interface SortingComparator<T> {
  sortingKey: string;
  comparator: Comparator<T>;
}

enum Orders {
  Asc = 'asc',
  Desc = 'desc',
}

function isNullOrUndefined(value: unknown): value is null | undefined {
  return value === null || value === undefined;
}

function isEmptyString(value: unknown): boolean {
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  return false;
}

function getDeepPropertyFromObject(
  obj: Record<string, any>,
  keys: string[],
  index = 0,
): unknown {
  if (!keys.length) {
    return obj;
  }
  const value = obj?.[keys[index]];
  if (value && !Array.isArray(value) && index < keys.length - 1) {
    return getDeepPropertyFromObject(value, keys, index + 1);
  }
  return value;
}

function getDeepValue(obj: Record<string, unknown>, keys: string[]) {
  if (keys.length === 1) {
    return obj[keys[0]];
  }
  return getDeepPropertyFromObject(obj, keys);
}

function getStringValueFromArray(
  array: Array<Record<string, unknown>>,
  key: string,
) {
  const string = array.map((item) => item[key]).join(', ');
  return string;
}

function defaultComparator(): Comparator<string | number | null | undefined> {
  return (a, b): ComparatorResult => {
    const isANullOrUndefOrEmptyStr = isNullOrUndefined(a) || isEmptyString(a);
    const isBNullOrUndefOrEmptyStr = isNullOrUndefined(b) || isEmptyString(b);

    if (isANullOrUndefOrEmptyStr && isBNullOrUndefOrEmptyStr) {
      return 0;
    }
    if (isANullOrUndefOrEmptyStr) {
      return -1;
    }
    if (isBNullOrUndefOrEmptyStr) {
      return 1;
    }
    if (a > b) {
      return 1;
    }
    if (a < b) {
      return -1;
    }
    return 0;
  };
}

function compareLetters(a: string, b: string): ComparatorResult {
  if (a === b) {
    return 0;
  }
  const aToUpper = a.toUpperCase();
  const bToUpper = b.toUpperCase();

  // A !== a && A === A
  if (a !== b && aToUpper === bToUpper) {
    return a < b ? 1 : -1;
  }
  // other cases
  return aToUpper > bToUpper ? 1 : -1;
}

function stringComparator(): Comparator<string> {
  return (a, b) => {
    const aTrimmed = a.trim();
    const bTrimmed = b.trim();
    if (aTrimmed === bTrimmed) {
      return 0;
    }

    if (isEmptyString(aTrimmed)) {
      return -1;
    }
    if (isEmptyString(bTrimmed)) {
      return 1;
    }
    // maximum number of cycle iterations
    const minLength = Math.min(aTrimmed.length, bTrimmed.length);

    const aSplitted = aTrimmed.split('');
    const bSplitted = bTrimmed.split('');

    let result: ComparatorResult = 0;

    for (let i = 0; i < minLength; i++) {
      if (result) break;
      result = compareLetters(aSplitted[i], bSplitted[i]);
    }
    if (!result) {
      result = aSplitted.length > bSplitted.length ? 1 : -1;
    }
    return result as ComparatorResult;
  };
}

function getSortOption(value: any, orderBy: string) {
  const keys = orderBy.split('.');

  const result = getDeepValue(value, keys);
  if (Array.isArray(result)) {
    return getStringValueFromArray(result, keys[keys.length - 1]);
  }
  return result;
}

function getComparator<T>(
  order: Order,
  customComparator?: Comparator<any>,
): Comparator<T> {
  return (a: T, b: T) => {
    let comparator: Comparator<any>;
    if (customComparator) {
      comparator = customComparator;
    } else if (typeof a === 'string' && typeof b === 'string') {
      comparator = stringComparator();
    } else {
      comparator = defaultComparator();
    }
    // if the direction is desc then return reverse result
    return (
      order === Orders.Asc ? comparator(a, b) : -comparator(a, b)
    ) as ComparatorResult;
  };
}

export function tableSort<T>(
  array: T[],
  order: Order,
  orderBy: string,
  sortingComparators?: Array<{
    sortingKey: string;
    comparator: Comparator<T>;
  }>,
) {
  return [...array].sort((a: T, b: T) => {
    const valueA = getSortOption(a, orderBy) as T;
    const valueB = getSortOption(b, orderBy) as T;

    if (
      (typeof valueA === 'object' && valueA !== null) ||
      (typeof valueB === 'object' && valueB !== null)
    ) {
      throw new Error('Sort option cannot be an object');
    }

    const customComparator = sortingComparators?.find(
      (c) => c.sortingKey === orderBy,
    )?.comparator;
    const comparator = getComparator(order, customComparator);
    return comparator(valueA, valueB);
  });
}

export function moveArrayElement<T>(from: number, to: number, array: T[]) {
  const newArray = [...array];
  newArray.splice(to, 0, newArray.splice(from, 1)[0]);
  return newArray;
}
